import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  ScanLine,
  Eye,
  Gauge,
  LayoutDashboard,
  Wand2,
  Sparkles,
  Layers,
  FileText,
  Mail,
  Gem,
  Send,
  MailCheck,
  UserRound,
  Linkedin,
  BarChart3,
  ChartBarBig,
  SlidersHorizontal,
  KeyRound,
  Library,
  MessageSquareText,
  Palette,
  GitCompareArrows,
  Bookmark,
  History,
  Download,
  Rocket,
} from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";

type Tool = { name: string; desc: string; icon: typeof Target };
type Category = {
  num: string;
  name: string;
  tagline: string;
  intro: string;
  tools: Tool[];
};

const CATEGORIES: Category[] = [
  {
    num: "01",
    name: "Qualify",
    tagline: "Should I Even Apply?",
    intro:
      "Most job seekers waste 60% of their time applying to jobs they were never going to win — or passing on jobs they were perfect for. This category answers the most important question in the entire job search before you spend a second tailoring or applying.",
    tools: [
      {
        name: "Qualifications Match Tool",
        icon: Target,
        desc: "Analyzes any job posting against your background and tells you instantly whether you are truly qualified, exactly what you have, exactly what you do not have, and whether you should pursue it. The real answer to: Am I qualified?",
      },
    ],
  },
  {
    num: "02",
    name: "Test",
    tagline: "Will My Resume Actually Pass?",
    intro:
      "Before you tailor anything, find out how your current resume performs against this specific job and the 2026 ATS algorithms running on 99% of the systems that will process your application.",
    tools: [
      {
        name: "ATS Algorithm Test (2026)",
        icon: ScanLine,
        desc: "Tests your resume against the exact filters used by 99% of employer platforms.",
      },
      {
        name: "Human Eye Test",
        icon: Eye,
        desc: "Tests whether your resume survives the 7 to 10 second scan and holds up under full review.",
      },
      {
        name: "Resume + ATS + Hiring Manager Match Test",
        icon: Gauge,
        desc: "Both standards in one combined score. If you score under 85%, you know before you submit.",
      },
      {
        name: "Match Score Dashboard",
        icon: LayoutDashboard,
        desc: "See every active application's match score at a glance.",
      },
    ],
  },
  {
    num: "03",
    name: "Build & Tailor",
    tagline: "The Core Engines",
    intro:
      "This is where TailorWiz does what no other tool on the market can do — produce documents at the level of an award-winning executive resume writer in under 60 seconds. Every time.",
    tools: [
      {
        name: "Resume Tailoring Tool",
        icon: Wand2,
        desc: "Tailors any resume to any job in under 60 seconds with 90% or better match every run.",
      },
      {
        name: "Advanced Tailoring Mode",
        icon: Sparkles,
        desc: "Layers in JobIntel 360 company intelligence to produce a resume that could only have been written for this specific company.",
      },
      {
        name: "Multi-Document Package Tailoring",
        icon: Layers,
        desc: "Resume, cover letter, Solution Asset, outreach letter, thank you letter, executive bio, LinkedIn summary — all in one run.",
      },
      {
        name: "ATS-Optimized Layout Engine",
        icon: FileText,
        desc: "Every document formatted to pass the algorithm AND impress the human.",
      },
    ],
  },
  {
    num: "04",
    name: "The Document Suite",
    tagline: "Beyond the Resume",
    intro:
      "Winning the job is a full document conversation. TailorWiz produces every single document required — all tailored to the same job, all telling the same story.",
    tools: [
      {
        name: "Cover Letter Generator",
        icon: Mail,
        desc: "Research-backed, opens with insider proof, closes as a peer.",
      },
      {
        name: "Solution Asset Generator",
        icon: Gem,
        desc: "The one-page positioning document with an 85% interview rate.",
      },
      {
        name: "Outreach Letter Generator",
        icon: Send,
        desc: "Direct outreach to the hiring manager that gets responses.",
      },
      {
        name: "Thank You Letter Generator",
        icon: MailCheck,
        desc: "Strategic post-interview reinforcement.",
      },
      {
        name: "Executive Bio Generator",
        icon: UserRound,
        desc: "Third-person narrative for boards, speaking, and media.",
      },
      {
        name: "LinkedIn Profile Wizard",
        icon: Linkedin,
        desc: "Complete profile rebuild that gets you found by the right people.",
      },
      {
        name: "LinkedIn Summary Generator",
        icon: Linkedin,
        desc: "Optimized to be found, read, and remembered.",
      },
    ],
  },
  {
    num: "05",
    name: "Intelligence Layers",
    tagline: "The Advanced Difference",
    intro:
      "These are the tools that make a resume look like it was written by someone who actually knows the company. Every other AI tool reads the job description. These tools read everything else.",
    tools: [
      {
        name: "Metrics Wizard",
        icon: BarChart3,
        desc: "Personal resume quantification. Generates field-specific questions to surface your real numbers.",
      },
      {
        name: "JD-Specific Metrics Wizard",
        icon: ChartBarBig,
        desc: "Generates questions based on what the company needs — not just what is on your resume.",
      },
      {
        name: "Advanced Tailoring Methods Selector",
        icon: SlidersHorizontal,
        desc: "Specialized tailoring approaches beyond keyword matching.",
      },
      {
        name: "Keyword Intelligence Panel",
        icon: KeyRound,
        desc: "Shows you every keyword, where it is weighted, and where your gaps are.",
      },
      {
        name: "Industry Language Library",
        icon: Library,
        desc: "Native vocabulary for dozens of industries.",
      },
    ],
  },
  {
    num: "06",
    name: "Control & Customization",
    tagline: "You Stay in the Driver's Seat",
    intro:
      "TailorWiz is powerful on autopilot — but you are never locked out. Steer every run as much or as little as you want.",
    tools: [
      {
        name: "Custom Instructions Field",
        icon: MessageSquareText,
        desc: "Type any instruction in plain language and TailorWiz follows it on the run.",
      },
      {
        name: "Writing Style Calibration",
        icon: Palette,
        desc: "Adjust tone, voice, and style across a full spectrum.",
      },
      {
        name: "Before and After Match Report",
        icon: GitCompareArrows,
        desc: "See every change made and why it improves your score.",
      },
    ],
  },
  {
    num: "07",
    name: "Track, Manage & Submit",
    tagline: "The Job Search Command Center",
    intro:
      "Every job, every document, every version — organized and ready to send from one place.",
    tools: [
      {
        name: "Saved Jobs Library",
        icon: Bookmark,
        desc: "Every job you run is saved automatically.",
      },
      {
        name: "Document Version History",
        icon: History,
        desc: "Every version of every document, every job, kept forever.",
      },
      {
        name: "One-Click Export",
        icon: Download,
        desc: "PDF, Word, or both — with a single click.",
      },
      {
        name: "Direct Apply Integration",
        icon: Rocket,
        desc: "Submit directly from TailorWiz with documents already attached.",
      },
    ],
  },
];

export default function Tools() {
  const totalTools = CATEGORIES.reduce((n, c) => n + c.tools.length, 0);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 75%, #4A78C2 0, transparent 38%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-16 pb-16 sm:pt-20 sm:pb-20 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            The Complete Tool Suite
          </span>
          <h1 className="mt-6 text-[34px] sm:text-[48px] lg:text-[56px] font-extrabold tracking-tight leading-[1.06] text-white">
            {totalTools} Tools. <span className="text-[#C9A84C]">7 Workflow Stages.</span>
            <br className="hidden sm:inline" /> One Platform.
          </h1>
          <p className="mt-6 text-[16px] sm:text-[19px] leading-[1.6] text-slate-200 max-w-3xl mx-auto">
            TailorWiz is not one tool. It is a complete suite of intelligent
            wizards, generators, and engines that work together to handle every
            part of the job application process — from qualification to
            submission.{" "}
            <span className="font-semibold text-white">
              All built on Marcus Hall's executive resume methodology. All
              optimized for the 2026 ATS.
            </span>
          </p>
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold tracking-tight shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              Try It Out FREE Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Workflow chips */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
            {CATEGORIES.map((c, i) => (
              <span key={c.num} className="flex items-center gap-2">
                <a
                  href={`#cat-${c.num}`}
                  className="text-[12px] sm:text-[13px] font-semibold text-slate-200 hover:text-[#C9A84C] border border-white/15 rounded-full px-3 py-1.5 transition-colors"
                >
                  {c.num} · {c.name}
                </a>
                {i < CATEGORIES.length - 1 && (
                  <ArrowRight className="w-3 h-3 text-[#C9A84C]/50 hidden sm:block" />
                )}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CATEGORY SECTIONS ============ */}
      {CATEGORIES.map((cat, idx) => (
        <section
          key={cat.num}
          id={`cat-${cat.num}`}
          className={`scroll-mt-20 py-16 sm:py-20 ${
            idx % 2 === 0 ? "bg-white" : "bg-slate-50"
          }`}
        >
          <div className="max-w-6xl mx-auto px-6">
            {/* Category header */}
            <div className="flex items-start gap-5 mb-10">
              <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#0B1C3F] flex items-center justify-center">
                <span className="text-[26px] sm:text-[32px] font-extrabold text-[#C9A84C]">
                  {cat.num}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[24px] sm:text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">
                  {cat.name}
                  <span className="text-slate-300 font-bold mx-2">/</span>
                  <span className="text-[#C9A84C]">{cat.tagline}</span>
                </h2>
                <p className="mt-2 text-[15px] sm:text-[16px] leading-[1.6] text-slate-600">
                  {cat.intro}
                </p>
              </div>
            </div>

            {/* Tool cards */}
            <div
              className={`grid gap-4 ${
                cat.tools.length === 1
                  ? "grid-cols-1 max-w-2xl"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
              }`}
            >
              {cat.tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <div
                    key={tool.name}
                    className="group rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 hover:border-[#C9A84C]/60 hover:shadow-lg transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#0B1C3F] text-[#C9A84C] flex items-center justify-center group-hover:bg-[#C9A84C] group-hover:text-white transition-colors">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="mt-4 text-[16px] sm:text-[17px] font-bold text-slate-900 leading-snug">
                      {tool.name}
                    </h3>
                    <p className="mt-2 text-[14px] leading-[1.55] text-slate-600">
                      {tool.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 sm:py-24 bg-gradient-to-b from-[#0B1C3F] to-[#0E2350] text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-[30px] sm:text-[42px] font-extrabold tracking-tight leading-[1.1]">
            Every Tool. <span className="text-[#C9A84C]">One Membership.</span>
          </h2>
          <p className="mt-5 text-[16px] sm:text-[18px] text-slate-200 leading-[1.6]">
            Get full access to all {totalTools} tools for 7 days. No credit card
            required. See for yourself why TailorWiz replaces every other tool in
            your job search.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              Try It Out FREE Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/how-it-works"
              className="text-[15px] font-semibold text-slate-200 hover:text-[#C9A84C] transition-colors"
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
